import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fileUtils } from '@/shared/lib';
import {
  useDownloadConsignmentReportMutation,
  useDownloadShippingLabelsMutation,
  useDownloadConsignmentTagMutation
} from '../api/consignmentsApi';

export type ConsignmentReportType = 'report' | 'labels' | 'tag';

export interface ConsignmentDownloadParams {
  id: string;
  lang?: string;
  opt?: number;
  o?: number;
  ext?: number;
}

/**
 * Hook to handle all consignment-related PDF/Report downloads.
 * Centralizes the mutation logic and browser-level blob handling.
 */
export function useConsignmentReports() {
  const { i18n } = useTranslation();
  const [downloadReport, { isLoading: isDownloadingReport }] = useDownloadConsignmentReportMutation();
  const [downloadLabels, { isLoading: isDownloadingLabels }] = useDownloadShippingLabelsMutation();
  const [downloadTag, { isLoading: isDownloadingTag }] = useDownloadConsignmentTagMutation();

  const download = useCallback(
    async (type: ConsignmentReportType, params: ConsignmentDownloadParams) => {
      const lang = params.lang || i18n.language || 'en';
      const opt = params.opt ?? 1;

      try {
        let blob: Blob;
        switch (type) {
          case 'report':
            blob = await downloadReport({ id: params.id, lang, opt }).unwrap();
            break;
          case 'labels':
            blob = await downloadLabels({ id: params.id, lang, opt, o: params.o }).unwrap();
            break;
          case 'tag':
            blob = await downloadTag({ id: params.id, lang, opt, ext: params.ext }).unwrap();
            break;
          default:
            throw new Error(`Unknown report type: ${type}`);
        }
        fileUtils.openInNewTab(blob);
      } catch (err) {
        console.error(`[useConsignmentReports] Failed to download ${type}`, err);
        throw err;
      }
    },
    [downloadReport, downloadLabels, downloadTag, i18n.language]
  );

  return {
    download,
    isDownloadingReport,
    isDownloadingLabels,
    isDownloadingTag,
    isLoading: isDownloadingReport || isDownloadingLabels || isDownloadingTag
  };
}
